import React from 'react';
import { ISinglePaymentPageProps } from './interfaces';
import { PaymentWidgetContainer } from '../components/PaymentWidget/PaymentWidgetContainer';
import { PaymentCheckoutContainer } from '../components/PaymentCheckout/PaymentCheckoutContainer';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { ApplyCouponToOrderResponse } from '@wix/ambassador-challenges-v1-participant/types';
import { PaymentJoinButton } from '../components/PaymentJoinButton/PaymentJoinButton';
import { FCWithChildren } from '@wix/challenges-web-library';
import { classes, st } from './Widget.st.css';
import { useUser } from '../../../contexts/User/UserContext';
import {
  Divider,
  Text,
  TextButton,
  TextTypography,
  Button,
  ButtonPriority,
  TextPriority,
} from 'wix-ui-tpa/cssVars';
import { getParticipantOrderId } from '../../../selectors/participants';
import { useLocation } from '../../../contexts/Location/LocationContext';
import { Check as CheckIcon } from 'wix-ui-icons-common';
import { useSettings } from '@wix/tpa-settings/react';
import challengeSettings from '../settingsParams';
import { getSecondaryTagName } from '../utils/a11y';

export const PaymentPageV1: FCWithChildren<ISinglePaymentPageProps> = () => {
  const { isViewer, isMobile, isRTL } = useEnvironment();
  const { t } = useTranslation();
  const { user, promptLogin, logout, participant, requestMemberEmail } =
    useUser();
  const { query } = useLocation();
  const settings = useSettings();
  const loggedIn = !!user?.loggedIn;
  const orderId = getParticipantOrderId(participant) || query?.orderId;

  const [coupon, setCoupon] = React.useState<ApplyCouponToOrderResponse>(null);
  const isCouponZeroPrice = parseFloat(coupon?.total) === 0;
  const [renderPaymentWidget, setRenderPaymentWidget] =
    React.useState<boolean>(false);
  const [isPaymentStarted, setIsPaymentStarted] =
    React.useState<boolean>(false);
  const primaryTagName = settings.get(challengeSettings.titleTag);
  const secondaryTagName = getSecondaryTagName(primaryTagName);

  React.useEffect(() => {
    requestMemberEmail?.();
  }, [requestMemberEmail]);

  React.useEffect(() => {
    // render payment widget after hydration
    // skip check for editor
    if (!isViewer || (orderId && loggedIn)) {
      setRenderPaymentWidget(true);
      window?.scrollTo?.(0, 0);
    }
  }, [orderId, loggedIn, isViewer]);

  return (
    <section
      data-hook="challenges-payment-page"
      className={st(classes.root, {
        mobile: isMobile,
        rtl: isRTL,
      })}
    >
      <div className={classes.paymentWidgetWrapper}>
        <Text
          priority={TextPriority.primary}
          tagName={primaryTagName}
          typography={TextTypography.largeTitle}
          className={classes.primaryTitle}
        >
          {settings.get(challengeSettings.titleText) as string}
        </Text>
        <div className={classes.divider}>
          <Divider className={classes.separator} />
        </div>
        {isCouponZeroPrice ? (
          <PaymentJoinButton />
        ) : (
          <>
            <div className={classes.paymentSectionContainer}>
              <div className={classes.titleWrapper}>
                <Text
                  typography={TextTypography.largeTitle}
                  tagName={secondaryTagName}
                  className={classes.title}
                >
                  {loggedIn ? <CheckIcon /> : <span>1. </span>}
                  <span>{t('checkout.title.sign-up')}</span>
                </Text>
                {loggedIn ? (
                  <TextButton onClick={() => logout()}>
                    {t('checkout.logout')}
                  </TextButton>
                ) : null}
              </div>
              {loggedIn ? (
                <div className={classes.signupTitleWrapper}>
                  <Text typography={TextTypography.runningText}>
                    {t('checkout.text.logged-in', {
                      memberEmail: user?.email,
                    })}
                  </Text>
                </div>
              ) : (
                <div className={classes.signupContainer}>
                  <Text>{t('checkout.sign-up.description')}</Text>
                  <div className={classes.signupButtonsWrapper}>
                    <Button
                      fullWidth
                      onClick={() => promptLogin({ mode: 'signup' })}
                    >
                      {t('checkout.title.sign-up')}
                    </Button>
                    <Button
                      fullWidth
                      onClick={() => promptLogin({ mode: 'login' })}
                      priority={ButtonPriority.secondary}
                    >
                      {t('checkout.login')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div
              className={
                !renderPaymentWidget ? classes.paymentWrapperDisabled : ''
              }
            >
              <div className={classes.divider}>
                <Divider className={classes.separator} />
              </div>
              <div className={classes.titleWrapper}>
                <Text
                  typography={TextTypography.largeTitle}
                  tagName={secondaryTagName}
                  className={classes.title}
                >
                  2. {t('checkout.title.payment')}
                </Text>
              </div>
            </div>
            {renderPaymentWidget && (
              <PaymentWidgetContainer
                hideTitle={true}
                onApiReady={() => {}}
                onWidgetLoaded={() => {}}
                onPaymentStart={() => {
                  console.log('onPaymentStart');
                  setIsPaymentStarted(true);
                }}
                onPaymentEnd={() => {
                  console.log('onPaymentEnd');
                  setIsPaymentStarted(false);
                }}
              />
            )}
          </>
        )}
      </div>
      <div className={classes.checkoutLayout}>
        <PaymentCheckoutContainer
          onCouponApplied={(_coupon) => {
            setCoupon(_coupon);
          }}
          isPaymentStarted={isPaymentStarted}
        />
      </div>
    </section>
  );
};
